/**
 * Escape, and the one stack that answers it.
 *
 * The rule, in one line: **the key closes the innermost thing that is open** —
 * a menu, a popover, a dialog, a sheet — and nothing else. Everything that can
 * be closed that way registers here ({@link escapable}); the key itself is
 * read in exactly one place, the shell's window listener, which asks
 * {@link dismissTopmost} whose it is.
 *
 * It replaces the chain of `else if`s the shell used to hold. A chain is a
 * priority list written by hand, and it was wrong the moment an overlay was
 * added somewhere else in the tree: the member menu inside the channel console
 * and the attachment lightbox inside a bubble are neither of them the shell's
 * business, and both of them are *over* whatever the chain would have closed.
 * The order here is not written down at all — it is the order things opened,
 * which is the only order the question has ever really had.
 *
 * The desktop client keeps the same rule in `ds/escape.rs`, down to the
 * `escapePending` escape hatch below.
 */

import { createEffect, onCleanup } from "solid-js";

interface Layer {
  close: () => void;
}

/** Every layer on screen, innermost last. */
const stack: Layer[] = [];

/**
 * Registers a layer as something Escape closes, for as long as `open()` is
 * true. Call it in component setup, once per overlay.
 */
export function escapable(open: () => boolean, close: () => void): void {
  createEffect(() => {
    if (!open()) return;
    const layer: Layer = { close };
    stack.push(layer);
    onCleanup(() => {
      const at = stack.lastIndexOf(layer);
      if (at >= 0) stack.splice(at, 1);
    });
  });
}

/** Closes the innermost open layer. Answers whether there was one. */
export function dismissTopmost(): boolean {
  // Taken off the stack here rather than left for the registration's cleanup:
  // every setter in the shell is wrapped in a view transition, which runs the
  // mutation at the next rendering opportunity, so the cleanup is one frame
  // late. Two quick presses have to reach two layers.
  const layer = stack.pop();
  if (!layer) return false;
  layer.close();
  return true;
}

/**
 * Whether Escape already has somewhere to go.
 *
 * For the few fields that read the key for themselves — a message being
 * edited, a channel being named. They stand down while something floats over
 * the page, so one press does one thing.
 */
export function escapePending(): boolean {
  return stack.length > 0;
}

/** Test seam: the layer count, so a test can assert registration and cleanup. */
export function openLayerCount(): number {
  return stack.length;
}
